import { sendEmailToChangeEmailApi } from "@/api/services/main/auth";
import { IconSend } from "@/assets/icons/IconSend";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Modal } from "@/components/elements/Modal";
import { TopPage } from "@/components/elements/TopPage";
import { TUserSettingEmailForm } from "@/features/user-setting/types/UserSettingEmailForm";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { divider, state, text } from "@/theme/colors";
import { setErrorForm, showError } from "@/utils/error";
import { Stack, Typography } from "@mui/material";
import { FC, useMemo, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TChangeEmailConfirmPageProps = {
  methods: UseFormReturn<TUserSettingEmailForm>;
  currentEmail: string;
  newEmail: string;
  onBack: () => void;
};

export const ChangeEmailConfirmPage: FC<TChangeEmailConfirmPageProps> = ({ methods, currentEmail, newEmail, onBack }) => {
  const showSuccessChangeEmailModal = useVisible();
  const navigate = useNavigate();
  const [isResend, setIsResend] = useState(false);

  const isSameEmail = useMemo(() => {
    return newEmail === currentEmail;
  }, [newEmail, currentEmail]);

  const handleChangeEmail = async (resend: boolean) => {
    try {
      await sendEmailToChangeEmailApi({ email: newEmail });
      setIsResend(resend);
      showSuccessChangeEmailModal.open();
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const handleBackToUserSetting = () => {
    showSuccessChangeEmailModal.close();
    navigate(AppRoutes.userSettingDetail);
  };

  return (
    <>
      <Stack
        gap={1}
        sx={{
          minHeight: "calc(100vh - 226px)",
        }}
      >
        <TopPage title="メールアドレス変更" enablePageBack={false} />
        <Stack mt={-1}>
          <Typography variant="body14" color={text.secondary}>
            メールアドレス変更にはメールによる認証が必要になります。 <br />
            認証用URLが記載されたメールを送信いたしますので、メールの内容に従って認証を実施してください。 <br />
            以下の内容で変更します。よろしければ「認証メールを送信」ボタンを押してください。
          </Typography>
        </Stack>
        <InnerBox padding={4} mt={3} showDivider={false} borderColor={divider.high}>
          <Stack gap={7}>
            <Stack gap={2.5}>
              <Stack gap={3}>
                <Stack flexDirection="row" alignItems="center" gap={8} paddingBottom={2} borderBottom={`1px solid ${divider.middle}`}>
                  <Typography variant="cap12">現在のメールアドレス</Typography>
                  <Typography variant="cap12Bold">{currentEmail}</Typography>
                </Stack>
                <Stack flexDirection="row" alignItems="center" gap={8} paddingBottom={2}>
                  <Typography variant="cap12">新しいメールアドレス</Typography>
                  <Typography variant="cap12Bold">{newEmail}</Typography>
                </Stack>
                {isSameEmail && (
                  <Typography variant="body14" color={text.error}>
                    新しいメールアドレスは現在のメールアドレスと同じです。別のメールアドレスを入力してください。
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Stack>
        </InnerBox>
      </Stack>

      <FooterContainer height="114px">
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" width="100%">
          <Button variant="ghost" onClick={onBack}>
            戻る
          </Button>
          <Stack direction="column" gap={1} alignItems="flex-end">
            <Button variant="primary" size="md" disabled={isSameEmail} onClick={() => handleChangeEmail(false)} startIcon={<IconSend fontSize={20} />}>
              認証メールを送信
            </Button>
            <Typography variant="body14" color={state.link_1} sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => handleChangeEmail(true)}>
              メールの再送信（ご案内メールが届かない場合）
            </Typography>
          </Stack>
        </Stack>
      </FooterContainer>

      <Modal
        width={417}
        title={isResend ? "認証メールを再送信しました" : "認証メールを送信しました"}
        isOpen={showSuccessChangeEmailModal.visible}
        enableFooter={false}
        onClose={showSuccessChangeEmailModal.close}
      >
        <Typography variant="cap12">認証用URLが記載されたメールを送信いたしました。</Typography>
        <Stack mt={3} alignItems="center">
          <Button variant="ghost" size="md" onClick={handleBackToUserSetting}>
            ユーザー設定に戻る
          </Button>
        </Stack>
      </Modal>
    </>
  );
};
